import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Clock, Send, CheckCircle, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { formatCT } from '../utils/time';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_SCHEDULE = {
  enabled: false,
  recipient: '',
  frequency: 'weekly',
  day_of_week: 1,
  hour: 2,
  minute: 0,
  last_sent_at: null,
  last_status: '',
  last_error: '',
};

const pad = (n) => String(n).padStart(2, '0');

const BackupScheduleCard = ({ token, API }) => {
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/backup/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedule({ ...DEFAULT_SCHEDULE, ...(res.data || {}) });
    } catch (e) {
      // keep defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (patch) => setSchedule((prev) => ({ ...prev, ...patch }));

  const saveSchedule = async () => {
    if (schedule.enabled && !schedule.recipient) {
      toast.error('Add a recipient email before enabling the schedule.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled: !!schedule.enabled,
        recipient: schedule.recipient || '',
        frequency: schedule.frequency,
        day_of_week: Number(schedule.day_of_week ?? 1),
        hour: Number(schedule.hour ?? 2),
        minute: Number(schedule.minute ?? 0),
      };
      const res = await axios.put(`${API}/backup/schedule`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedule({ ...DEFAULT_SCHEDULE, ...(res.data || {}) });
      toast.success('Backup schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const res = await axios.post(
        `${API}/backup/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Backup emailed to ${res.data?.recipient}`);
      fetchSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send backup');
      fetchSchedule();
    } finally {
      setSendingNow(false);
    }
  };

  const lastSent = schedule.last_sent_at ? new Date(schedule.last_sent_at) : null;

  return (
    <div className="bg-gray-50 rounded-lg p-6" data-testid="backup-schedule-card">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-cyan-100 rounded-lg">
          <Mail className="w-6 h-6 text-cyan-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-1">
            <h3 className="font-semibold text-navy-900">Email Backup Schedule</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {schedule.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={!!schedule.enabled}
                onCheckedChange={(v) => update({ enabled: !!v })}
                data-testid="backup-schedule-enabled-toggle"
              />
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Automatically email a JSON backup to the address below. Times are <strong>Central Time</strong> (CT, daylight-saving aware).
            SMTP must be configured on the SMTP Settings tab.
          </p>

          {loading ? (
            <p className="text-sm text-gray-500 py-4">Loading…</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-navy-900 mb-1">
                    Recipient
                  </label>
                  <Input
                    type="email"
                    value={schedule.recipient || ''}
                    onChange={(e) => update({ recipient: e.target.value })}
                    placeholder="backups@example.com"
                    data-testid="backup-schedule-recipient-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-navy-900 mb-1">
                    Frequency
                  </label>
                  <div className="flex gap-2">
                    {['daily', 'weekly'].map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => update({ frequency: f })}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                          schedule.frequency === f
                            ? 'bg-navy-900 text-white border-navy-900'
                            : 'bg-white text-navy-900 border-gray-300 hover:border-gold-400'
                        }`}
                        data-testid={`backup-schedule-frequency-${f}`}
                      >
                        {f === 'daily' ? 'Daily' : 'Weekly'}
                      </button>
                    ))}
                  </div>
                </div>

                {schedule.frequency === 'weekly' && (
                  <div>
                    <label className="block text-xs font-medium text-navy-900 mb-1">
                      Day of week
                    </label>
                    <div className="flex gap-1">
                      {DOW_LABELS.map((label, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => update({ day_of_week: i })}
                          className={`flex-1 px-1.5 py-2 rounded-md text-xs font-medium border transition-colors ${
                            Number(schedule.day_of_week) === i
                              ? 'bg-gold-500 text-navy-900 border-gold-500'
                              : 'bg-white text-navy-900 border-gray-300 hover:border-gold-400'
                          }`}
                          data-testid={`backup-schedule-dow-${i}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={schedule.frequency === 'weekly' ? '' : 'sm:col-start-2'}>
                  <label className="block text-xs font-medium text-navy-900 mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Time (CT)
                  </label>
                  <input
                    type="time"
                    value={`${pad(schedule.hour)}:${pad(schedule.minute)}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map((x) => parseInt(x, 10));
                      update({ hour: h || 0, minute: m || 0 });
                    }}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-navy-900 focus:border-gold-500 focus:outline-none"
                    data-testid="backup-schedule-time-input"
                  />
                </div>
              </div>

              {/* Status row */}
              <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
                {lastSent ? (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${
                      schedule.last_status === 'success'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                    data-testid="backup-schedule-last-sent"
                  >
                    {schedule.last_status === 'success' ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <XCircle className="w-3 h-3" />
                    )}
                    Last sent: {formatCT(lastSent)} CT
                  </span>
                ) : (
                  <span className="text-gray-500">No backup emails sent yet.</span>
                )}
                {schedule.last_status === 'error' && schedule.last_error && (
                  <span className="text-red-600 font-mono break-all">{schedule.last_error}</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={saveSchedule}
                  disabled={saving}
                  className="bg-cyan-700 hover:bg-cyan-800 text-white"
                  data-testid="backup-schedule-save-btn"
                >
                  {saving ? 'Saving…' : 'Save Schedule'}
                </Button>
                <Button
                  onClick={sendNow}
                  disabled={sendingNow || !schedule.recipient}
                  variant="outline"
                  className="border-cyan-700 text-cyan-700 hover:bg-cyan-50"
                  data-testid="backup-schedule-send-now-btn"
                >
                  {sendingNow ? 'Sending…' : 'Send Now'}
                  <Send className="w-3.5 h-3.5 ml-2" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupScheduleCard;
